import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Plus, Trash2, Image as ImageIcon, Upload, X, Save, GripVertical, Play, Film } from 'lucide-react'
import { supabase, uploadFile, getPublicUrl } from '@/lib/supabase'
import type { GalleryImage, GalleryTag } from '@/types'

// Sortable Gallery Image Item
interface SortableGalleryItemProps {
  image: GalleryImage
  onSelect: (image: GalleryImage) => void
  onDelete: (id: string) => void
}

function SortableGalleryItem({ image, onSelect, onDelete }: SortableGalleryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: image.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 1,
  }

  const isVideo = image.media_type === 'video'

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative aspect-square rounded-lg overflow-hidden group cursor-pointer ${isDragging ? 'shadow-lg' : ''}`}
      onClick={() => onSelect(image)}
    >
      {isVideo ? (
        <>
          <video
            src={image.image_url}
            className="w-full h-full object-cover"
            muted
            playsInline
          />
          {/* Video indicator */}
          <div className="absolute top-2 left-2 p-1.5 bg-black/70 rounded pointer-events-none">
            <Film className="w-4 h-4 text-white" />
          </div>
          {/* Play button overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-12 h-12 bg-black/50 rounded-full flex items-center justify-center">
              <Play className="w-6 h-6 text-white ml-1" fill="white" />
            </div>
          </div>
        </>
      ) : (
        <img src={image.image_url} alt={image.title || ''} className="w-full h-full object-cover" />
      )}
      {/* Drag handle */}
      <div
        {...attributes}
        {...listeners}
        className="absolute top-2 right-2 p-1.5 bg-white/80 rounded cursor-grab active:cursor-grabbing opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4 text-secondary-500" />
      </div>
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 pointer-events-none">
        <button
          onClick={(e) => {
            e.stopPropagation()
            if (confirm('Delete this media?')) onDelete(image.id)
          }}
          className="p-2 bg-white rounded-full text-red-500 pointer-events-auto"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {image.tags && image.tags.length > 0 && (
        <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 pointer-events-none">
          {image.tags.slice(0, 2).map((tag) => (
            <span key={tag.id} className="badge bg-white/90 text-secondary-700 text-xs">
              {tag.name}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}

export function GalleryPage() {
  const [isUploading, setIsUploading] = useState(false)
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null)
  const [newTagName, setNewTagName] = useState('')
  const queryClient = useQueryClient()

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const { data: images, isLoading } = useQuery({
    queryKey: ['admin-gallery'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('gallery_images')
        .select(`
          *,
          tags:gallery_image_tags(
            tag:gallery_tags(*)
          )
        `)
        .order('sort_order')
      if (error) throw error
      return data.map((img) => ({
        ...img,
        tags: img.tags?.map((t: { tag: GalleryTag }) => t.tag) || [],
      })) as GalleryImage[]
    },
  })

  const { data: allTags } = useQuery({
    queryKey: ['admin-gallery-tags'],
    queryFn: async () => {
      const { data, error } = await supabase.from('gallery_tags').select('*').order('name')
      if (error) throw error
      return data as GalleryTag[]
    },
  })

  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      for (const file of Array.from(files)) {
        const isVideo = file.type.startsWith('video/')
        const path = `gallery/${Date.now()}-${file.name}`
        await uploadFile('gallery', path, file)
        const url = getPublicUrl('gallery', path)
        await supabase.from('gallery_images').insert({
          image_url: url,
          title: file.name.replace(/\.[^/.]+$/, ''),
          sort_order: images?.length || 0,
          media_type: isVideo ? 'video' : 'image',
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
      setIsUploading(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('gallery_images').delete().eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
    },
  })

  const updateImageMutation = useMutation({
    mutationFn: async ({ id, title, description }: { id: string; title: string; description: string }) => {
      await supabase.from('gallery_images').update({ title, description }).eq('id', id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
    },
  })

  const addTagMutation = useMutation({
    mutationFn: async (name: string) => {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-')
      await supabase.from('gallery_tags').insert({ name, slug })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery-tags'] })
      setNewTagName('')
    },
  })

  const deleteTagMutation = useMutation({
    mutationFn: async (tagId: string) => {
      // First delete all image-tag associations
      await supabase.from('gallery_image_tags').delete().eq('tag_id', tagId)
      // Then delete the tag itself
      await supabase.from('gallery_tags').delete().eq('id', tagId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery-tags'] })
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
    },
  })

  const toggleImageTagMutation = useMutation({
    mutationFn: async ({ imageId, tagId, add }: { imageId: string; tagId: string; add: boolean }) => {
      if (add) {
        await supabase.from('gallery_image_tags').insert({ image_id: imageId, tag_id: tagId })
      } else {
        await supabase.from('gallery_image_tags').delete().eq('image_id', imageId).eq('tag_id', tagId)
      }
      return { tagId, add }
    },
    onSuccess: ({ tagId, add }) => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
      // Update selectedImage state to reflect the change immediately
      if (selectedImage) {
        const tag = allTags?.find((t) => t.id === tagId)
        if (tag) {
          if (add) {
            setSelectedImage({
              ...selectedImage,
              tags: [...(selectedImage.tags || []), tag],
            })
          } else {
            setSelectedImage({
              ...selectedImage,
              tags: selectedImage.tags?.filter((t) => t.id !== tagId) || [],
            })
          }
        }
      }
    },
  })

  const reorderMutation = useMutation({
    mutationFn: async (reorderedImages: GalleryImage[]) => {
      const updates = reorderedImages.map((img, idx) => ({
        id: img.id,
        sort_order: idx,
      }))
      for (const update of updates) {
        await supabase.from('gallery_images').update({ sort_order: update.sort_order }).eq('id', update.id)
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-gallery'] })
    },
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id && images) {
      const oldIndex = images.findIndex((img) => img.id === active.id)
      const newIndex = images.findIndex((img) => img.id === over.id)
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedImages = arrayMove(images, oldIndex, newIndex)
        reorderMutation.mutate(reorderedImages)
      }
    }
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setIsUploading(true)
      uploadMutation.mutate(e.target.files)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Gallery</h1>
          <p className="text-secondary-600">Manage gallery images, videos and tags</p>
        </div>
        <label className="btn-primary cursor-pointer">
          <Upload className="w-5 h-5 mr-2" />
          Upload Media
          <input type="file" accept="image/*,video/*" multiple onChange={handleFileUpload} className="sr-only" />
        </label>
      </div>

      {/* Tags Management */}
      <div className="card p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-secondary-700">Tags:</span>
          {allTags?.map((tag) => (
            <span key={tag.id} className="badge bg-secondary-100 text-secondary-700 flex items-center gap-1.5 pr-1">
              {tag.name}
              <button
                onClick={() => {
                  if (confirm(`Delete tag "${tag.name}"? This will remove it from all images.`)) {
                    deleteTagMutation.mutate(tag.id)
                  }
                }}
                className="p-0.5 hover:bg-secondary-200 rounded-full transition-colors"
                title="Delete tag"
              >
                <X className="w-3 h-3 text-secondary-500 hover:text-red-500" />
              </button>
            </span>
          ))}
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="New tag"
              className="input w-32 h-8 text-sm"
            />
            <button
              onClick={() => newTagName && addTagMutation.mutate(newTagName)}
              disabled={!newTagName}
              className="btn-primary btn-sm"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Image Grid */}
      <div className="card">
        <div className="flex items-center justify-between p-4 border-b">
          <span className="text-sm text-secondary-500">
            {images?.length || 0} items
            {images && images.length > 0 && (
              <span className="text-secondary-400 ml-2">
                ({images.filter(i => i.media_type === 'image').length} images, {images.filter(i => i.media_type === 'video').length} videos)
              </span>
            )}
          </span>
          <span className="text-xs text-secondary-400">Drag to reorder</span>
        </div>
        {isLoading || isUploading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            <p className="text-secondary-500 mt-2">{isUploading ? 'Uploading...' : 'Loading...'}</p>
          </div>
        ) : images && images.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={images.map((img) => img.id)}
              strategy={rectSortingStrategy}
            >
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-4">
                {images.map((image) => (
                  <SortableGalleryItem
                    key={image.id}
                    image={image}
                    onSelect={setSelectedImage}
                    onDelete={(id) => deleteMutation.mutate(id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="p-8 text-center">
            <ImageIcon className="w-12 h-12 text-secondary-300 mx-auto mb-4" />
            <p className="text-secondary-500">No images yet</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {selectedImage && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedImage(null)}
              className="fixed inset-0 bg-black/50 z-40"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-4 sm:inset-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-2xl bg-white rounded-xl shadow-xl z-50 overflow-hidden"
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold">
                  Edit {selectedImage.media_type === 'video' ? 'Video' : 'Image'}
                </h2>
                <button onClick={() => setSelectedImage(null)} className="p-2 hover:bg-secondary-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-6">
                {selectedImage.media_type === 'video' ? (
                  <video
                    src={selectedImage.image_url}
                    controls
                    className="w-full aspect-square object-cover rounded-lg bg-black"
                  />
                ) : (
                  <img src={selectedImage.image_url} alt="" className="w-full aspect-square object-cover rounded-lg" />
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Title</label>
                    <input
                      type="text"
                      defaultValue={selectedImage.title || ''}
                      onChange={(e) => setSelectedImage({ ...selectedImage, title: e.target.value })}
                      className="input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-1">Description</label>
                    <textarea
                      defaultValue={selectedImage.description || ''}
                      onChange={(e) => setSelectedImage({ ...selectedImage, description: e.target.value })}
                      rows={3}
                      className="input resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 mb-2">Tags</label>
                    <div className="flex flex-wrap gap-2">
                      {allTags?.map((tag) => {
                        const isSelected = selectedImage.tags?.some((t) => t.id === tag.id)
                        return (
                          <button
                            key={tag.id}
                            onClick={() =>
                              toggleImageTagMutation.mutate({
                                imageId: selectedImage.id,
                                tagId: tag.id,
                                add: !isSelected,
                              })
                            }
                            className={`badge ${
                              isSelected ? 'bg-primary-600 text-white' : 'bg-secondary-100 text-secondary-700'
                            }`}
                          >
                            {tag.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      updateImageMutation.mutate({
                        id: selectedImage.id,
                        title: selectedImage.title || '',
                        description: selectedImage.description || '',
                      })
                      setSelectedImage(null)
                    }}
                    className="btn-primary w-full"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  )
}
